import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ApiError } from '@/types';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const INDUSTRIES = [
  'Accounting',
  'Automotive',
  'Business Services',
  'Child/Senior Care',
  'Construction',
  'Counseling/Mental Health',
  'Dental/Orthodontics',
  'E-Commerce',
  'Education',
  'Energy/Utilities',
  'Entertainment',
  'Event Planning',
  'Finance',
  'Food & Beverage',
  'Government',
  'Home Services',
  'Insurance',
  'Law/Legal Services',
  'Manufacturing',
  'Marketing/SEO',
  'Medical',
  'Miscellaneous',
  'Non Profit',
  'Other',
  'Personal Care/Wellness',
  'Pet Care/Veterinarian',
  'Real Estate',
  'Retail',
  'Technology',
  'Transportation',
  'Travel/Leisure',
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

interface FormData {
  Account_Name: string;
  Industry: string;
  Is_AI_Receptionist_Free_Trial: boolean;
  First_Name: string;
  Last_Name: string;
  Phone: string;
  Email: string;
  Street: string;
  City: string;
  State: string;
  Postal_Code: string;
  Country: string;
  AI_Receptionist_Allowance: number;
}

const AI_ALLOWANCE_OPTIONS = ['50', '100', '200', '500', 'Enterprise'] as const;

// Format phone number as (nnn) nnn-nnnn
function formatPhoneNumber(value: string): string {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');

  // Limit to 10 digits
  const limited = digits.slice(0, 10);

  // Format based on length
  if (limited.length === 0) return '';
  if (limited.length <= 3) return `(${limited}`;
  if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
}

// Strip phone formatting for API submission
function stripPhoneFormatting(value: string): string {
  return value.replace(/\D/g, '');
}

// Map field paths to user-friendly labels
const FIELD_LABELS: Record<string, string> = {
  Account_Name: 'Account Name',
  Industry: 'Industry',
  First_Name: 'First Name',
  Last_Name: 'Last Name',
  Phone: 'Phone',
  Email: 'Email',
  Street: 'Street',
  City: 'City',
  State: 'State',
  Postal_Code: 'Postal Code',
  AI_Receptionist_Allowance: 'AI Receptionist Allowance',
  allowances: 'AI Receptionist Allowance',
};

export function NewRequestPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [allowanceOption, setAllowanceOption] = useState<string>('');
  const [enterpriseAllowance, setEnterpriseAllowance] = useState<string>('');
  const [formData, setFormData] = useState<FormData>({
    Account_Name: '',
    Industry: '',
    Is_AI_Receptionist_Free_Trial: false,
    First_Name: '',
    Last_Name: '',
    Phone: '',
    Email: '',
    Street: '',
    City: '',
    State: '',
    Postal_Code: '',
    Country: 'US',
    AI_Receptionist_Allowance: 0,
  });

  const handleChange = (field: keyof FormData, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const handleAllowanceChange = (value: string) => {
    setAllowanceOption(value);
    if (value === 'Enterprise') {
      handleChange('AI_Receptionist_Allowance', parseInt(enterpriseAllowance) || 0);
    } else {
      handleChange('AI_Receptionist_Allowance', parseInt(value) || 0);
    }
  };

  const handleEnterpriseAllowanceChange = (value: string) => {
    setEnterpriseAllowance(value);
    handleChange('AI_Receptionist_Allowance', parseInt(value) || 0);
  };

  const handleSubmit = async (e: React.FormEvent, submitImmediately = false) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setIsLoading(true);

    try {
      // Validate allowance is selected
      if (!allowanceOption) {
        setError('Please select an AI Receptionist allowance');
        setFieldErrors({ AI_Receptionist_Allowance: 'Please select an allowance' });
        setIsLoading(false);
        return;
      }

      // Validate Enterprise allowance is over 500 and at most 100,000
      if (allowanceOption === 'Enterprise') {
        const enterpriseValue = parseInt(enterpriseAllowance) || 0;
        if (enterpriseValue <= 500) {
          setError('Enterprise Allowance: Must be greater than 500');
          setFieldErrors({ Enterprise_Allowance: 'Must be greater than 500' });
          setIsLoading(false);
          return;
        }
        if (enterpriseValue > 100000) {
          setError('Enterprise Allowance: Cannot exceed 100,000');
          setFieldErrors({ Enterprise_Allowance: 'Cannot exceed 100,000' });
          setIsLoading(false);
          return;
        }
      }

      // Validate required fields
      const newFieldErrors: Record<string, string> = {};
      const phoneDigits = stripPhoneFormatting(formData.Phone);
      if (!phoneDigits) {
        newFieldErrors.Phone = 'Phone number is required';
      } else if (phoneDigits.length !== 10) {
        newFieldErrors.Phone = 'Phone number must be exactly 10 digits';
      }
      if (!formData.Street.trim()) {
        newFieldErrors.Street = 'Street address is required';
      }
      if (!formData.City.trim()) {
        newFieldErrors.City = 'City is required';
      }
      if (!formData.State) {
        newFieldErrors.State = 'State is required';
      }
      if (!formData.Postal_Code.trim()) {
        newFieldErrors.Postal_Code = 'Postal code is required';
      }

      if (Object.keys(newFieldErrors).length > 0) {
        setFieldErrors(newFieldErrors);
        const errorFields = Object.keys(newFieldErrors).map(f => FIELD_LABELS[f] || f).join(', ');
        setError(`Please fix the following fields: ${errorFields}`);
        setIsLoading(false);
        return;
      }

      // Prepare payload with stripped phone number for API
      const apiPayload = {
        ...formData,
        Phone: stripPhoneFormatting(formData.Phone),
      };

      const request = await api.createRequest(apiPayload as unknown as Record<string, unknown>);

      if (submitImmediately) {
        await api.submitRequest(request.id);
      }

      navigate(`/requests/${request.id}`);
    } catch (err) {
      const apiError = err as ApiError;

      // Parse field-level errors from API response
      if (apiError.details && Array.isArray(apiError.details)) {
        const newFieldErrors: Record<string, string> = {};
        const errorMessages: string[] = [];

        for (const detail of apiError.details) {
          const fieldName = detail.path?.[0] || 'unknown';
          const fieldLabel = FIELD_LABELS[fieldName] || fieldName;
          newFieldErrors[fieldName] = detail.message;
          errorMessages.push(`${fieldLabel}: ${detail.message}`);
        }

        setFieldErrors(newFieldErrors);
        setError(errorMessages.join('. '));
      } else {
        setError(apiError.error || 'Failed to create request');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">New Request</h1>
            <p className="text-gray-600 mt-1">Create a new provisioning request</p>
          </div>
        </div>

        <form onSubmit={(e) => handleSubmit(e, false)}>
          {error && (
            <div className="rounded-lg bg-red-50 p-4 text-sm text-red-600 mb-6">
              {error}
            </div>
          )}

          {/* Account Information */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
              <CardDescription>Basic information about the client account</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="Account_Name">Account Name *</Label>
                  <Input
                    id="Account_Name"
                    value={formData.Account_Name}
                    onChange={(e) => handleChange('Account_Name', e.target.value)}
                    maxLength={255}
                    required
                    className={fieldErrors.Account_Name ? 'border-red-500' : ''}
                  />
                  {fieldErrors.Account_Name && (
                    <p className="text-sm text-red-600">{fieldErrors.Account_Name}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="Industry">Industry *</Label>
                  <Select
                    value={formData.Industry}
                    onValueChange={(value) => handleChange('Industry', value)}
                  >
                    <SelectTrigger className={fieldErrors.Industry ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map((industry) => (
                        <SelectItem key={industry} value={industry}>
                          {industry}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldErrors.Industry && (
                    <p className="text-sm text-red-600">{fieldErrors.Industry}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
              <CardDescription>Primary contact for this account</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="First_Name">First Name *</Label>
                  <Input
                    id="First_Name"
                    value={formData.First_Name}
                    onChange={(e) => handleChange('First_Name', e.target.value)}
                    maxLength={40}
                    required
                    className={fieldErrors.First_Name ? 'border-red-500' : ''}
                  />
                  {fieldErrors.First_Name && (
                    <p className="text-sm text-red-600">{fieldErrors.First_Name}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="Last_Name">Last Name *</Label>
                  <Input
                    id="Last_Name"
                    value={formData.Last_Name}
                    onChange={(e) => handleChange('Last_Name', e.target.value)}
                    maxLength={40}
                    required
                    className={fieldErrors.Last_Name ? 'border-red-500' : ''}
                  />
                  {fieldErrors.Last_Name && (
                    <p className="text-sm text-red-600">{fieldErrors.Last_Name}</p>
                  )}
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="Email">Email *</Label>
                  <Input
                    id="Email"
                    type="email"
                    value={formData.Email}
                    onChange={(e) => handleChange('Email', e.target.value)}
                    maxLength={80}
                    required
                    className={fieldErrors.Email ? 'border-red-500' : ''}
                  />
                  {fieldErrors.Email && (
                    <p className="text-sm text-red-600">{fieldErrors.Email}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="Phone">Phone *</Label>
                  <Input
                    id="Phone"
                    type="tel"
                    value={formData.Phone}
                    onChange={(e) => handleChange('Phone', formatPhoneNumber(e.target.value))}
                    placeholder="(702) 555-1234"
                    required
                    className={fieldErrors.Phone ? 'border-red-500' : ''}
                  />
                  {fieldErrors.Phone && (
                    <p className="text-sm text-red-600">{fieldErrors.Phone}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Address</CardTitle>
              <CardDescription>Business address</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="Street">Street *</Label>
                <Input
                  id="Street"
                  value={formData.Street}
                  onChange={(e) => handleChange('Street', e.target.value)}
                  maxLength={255}
                  required
                  className={fieldErrors.Street ? 'border-red-500' : ''}
                />
                {fieldErrors.Street && (
                  <p className="text-sm text-red-600">{fieldErrors.Street}</p>
                )}
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="City">City *</Label>
                  <Input
                    id="City"
                    value={formData.City}
                    onChange={(e) => handleChange('City', e.target.value)}
                    maxLength={40}
                    required
                    className={fieldErrors.City ? 'border-red-500' : ''}
                  />
                  {fieldErrors.City && (
                    <p className="text-sm text-red-600">{fieldErrors.City}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="State">State *</Label>
                  <Select
                    value={formData.State}
                    onValueChange={(value) => handleChange('State', value)}
                    required
                  >
                    <SelectTrigger className={fieldErrors.State ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldErrors.State && (
                    <p className="text-sm text-red-600">{fieldErrors.State}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="Postal_Code">Postal Code *</Label>
                  <Input
                    id="Postal_Code"
                    value={formData.Postal_Code}
                    onChange={(e) => handleChange('Postal_Code', e.target.value)}
                    maxLength={5}
                    required
                    className={fieldErrors.Postal_Code ? 'border-red-500' : ''}
                  />
                  {fieldErrors.Postal_Code && (
                    <p className="text-sm text-red-600">{fieldErrors.Postal_Code}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Service Allowance */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Service Allowance</CardTitle>
              <CardDescription>
                AI Receptionist service configuration
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="AI_Receptionist_Allowance">AI Receptionist Allowance *</Label>
                  <Select
                    value={allowanceOption}
                    onValueChange={(value) => {
                      handleAllowanceChange(value);
                      // Clear allowance errors when selection changes
                      if (fieldErrors.AI_Receptionist_Allowance || fieldErrors.allowances) {
                        setFieldErrors((prev) => {
                          const updated = { ...prev };
                          delete updated.AI_Receptionist_Allowance;
                          delete updated.allowances;
                          return updated;
                        });
                      }
                    }}
                  >
                    <SelectTrigger className={fieldErrors.AI_Receptionist_Allowance || fieldErrors.allowances ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Select allowance" />
                    </SelectTrigger>
                    <SelectContent>
                      {AI_ALLOWANCE_OPTIONS.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(fieldErrors.AI_Receptionist_Allowance || fieldErrors.allowances) && (
                    <p className="text-sm text-red-600">{fieldErrors.AI_Receptionist_Allowance || fieldErrors.allowances}</p>
                  )}
                </div>
                {allowanceOption === 'Enterprise' && (
                  <div className="space-y-2">
                    <Label htmlFor="Enterprise_Allowance">Enterprise Allowance (501+) *</Label>
                    <Input
                      id="Enterprise_Allowance"
                      type="number"
                      value={enterpriseAllowance}
                      onChange={(e) => {
                        handleEnterpriseAllowanceChange(e.target.value);
                        // Clear error when user types
                        if (fieldErrors.Enterprise_Allowance) {
                          setFieldErrors((prev) => {
                            const updated = { ...prev };
                            delete updated.Enterprise_Allowance;
                            return updated;
                          });
                        }
                      }}
                      placeholder="Enter a value greater than 500"
                      min={501}
                      max={100000}
                      className={fieldErrors.Enterprise_Allowance ? 'border-red-500' : ''}
                    />
                    {fieldErrors.Enterprise_Allowance && (
                      <p className="text-sm text-red-600">{fieldErrors.Enterprise_Allowance}</p>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="Is_AI_Receptionist_Free_Trial"
                  checked={formData.Is_AI_Receptionist_Free_Trial}
                  onChange={(e) => handleChange('Is_AI_Receptionist_Free_Trial', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-abby-primary focus:ring-abby-primary"
                />
                <Label htmlFor="Is_AI_Receptionist_Free_Trial">
                  AI Receptionist Free Trial
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-4 justify-end">
            <Button type="button" variant="outline" onClick={() => navigate('/')}>
              Cancel
            </Button>
            <Button type="submit" variant="secondary" disabled={isLoading}>
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save as Draft
            </Button>
            <Button
              type="button"
              disabled={isLoading}
              onClick={(e) => handleSubmit(e, true)}
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save & Submit
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
